import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import TechnikaServiceApi from '../api/JsdhNotifikaceService/service/TechnikaService';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { Udalost, Technika, Odpoved, UcastConfig, PoplachTechnikaResponse, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';
import { QueryError } from '../api/QueryError';
import TechnikaFoto from './TechnikaFoto';
import DojezdOdpocet from './DojezdOdpocet';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';
import { tintNestiha } from '../theme/nestihaTint';

interface TechnikaUcastWrapperProps {
    aktivniPoplach: Udalost;
    deviceToken?: string;
    ucastConfig?: UcastConfig;
    displejStyl?: DisplejStylOdpovedi;
    adaptivniLayout?: boolean;
}

const UCAST_CARD_SX = {
    p: 0.75,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1.5,
    flex: '1 1 calc(33.333% - 8px)',
    maxWidth: 'calc(33.333% - 6px)',
    minWidth: 200,
} as const;

const getTechnikaCardSx = (count: number) => ({
    p: 0.75,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: count === 3 ? 1 : 1.5,
    flex: count === 3 ? '1 1 calc(22.222% - 8px)' : '1 1 calc(33.333% - 8px)',
    maxWidth: count === 3 ? 'calc(22.222% - 6px)' : 'calc(33.333% - 6px)',
    minWidth: count === 3 ? 150 : 200,
}) as const;

const TechnikaUcastWrapper: React.FC<TechnikaUcastWrapperProps> = ({
    aktivniPoplach,
    deviceToken,
    ucastConfig,
    displejStyl = 'STRIPE',
    adaptivniLayout = false,
}) => {
    const showSemafor = ucastConfig?.semafor !== false;
    const showRole = ucastConfig?.role !== false;
    const showSpecializace = ucastConfig?.specializace !== false;
    const isResponzivni = ucastConfig?.responzivni !== false;

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const stripeJedu = '#2e7d32';
    const stripeNejedu = '#c62828';
    const sectionTintJedu = isDark ? 'rgba(76, 175, 80, 0.12)' : 'rgba(46, 125, 50, 0.10)';
    const sectionTintNejedu = isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(198, 40, 40, 0.10)';
    const cardTintJedu = isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(46, 125, 50, 0.18)';
    const cardTintNejedu = isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(198, 40, 40, 0.18)';

    const podkladNestiha = tintNestiha(theme, 0.18);

    const poplachId = useMemo(() => aktivniPoplach?.id, [aktivniPoplach?.id]);

    // Query na techniku
    const { data: allTechnika } = useQuery<Technika[]>({
        queryKey: ['technika', 'active', deviceToken],
        queryFn: () =>
            deviceToken
                ? TechnikaServiceApi.getActiveTechnikaWithDeviceToken(deviceToken)
                : TechnikaServiceApi.getActiveTechnika(),
        // Kiosk bez obsluhy — viz DashboardPage: bez intervalu zustane seznam po jednom
        // selhanem nacteni prazdny az do rucniho reloadu stranky.
        refetchInterval: 15000,
        staleTime: 10000,
        refetchOnWindowFocus: false,
    });

    // Query na odpovědi (polling 4s)
    const { data: odpovedi = [] } = useQuery<Odpoved[], QueryError>({
        queryKey: ['odpovedi', poplachId, deviceToken],
        queryFn: () =>
            poplachId
                ? JsdhNotifikaceService.getOdpovediByPoplachId(poplachId, deviceToken || undefined)
                : Promise.resolve([]),
        enabled: Boolean(poplachId),
        refetchInterval: 4000,
        refetchOnWindowFocus: false,
        staleTime: 3000,
    });

    // HO-162: hasic, jehoz ohlaseny dojezd je po limitu vyjezdu jednotky, je oranzovy
    // do sveho ohlaseneho prijezdu.
    const jeNestihajici = useNestihaVyjezd(aktivniPoplach.casVyhlaseni, odpovedi);

    // Query na detaily techniky s info o zdroji (KOPIS/VELITEL)
    const { data: technikaSources } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', poplachId, deviceToken],
        queryFn: () => JsdhNotifikaceService.getUdalostTechnika(poplachId!, deviceToken || undefined),
        enabled: Boolean(poplachId),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    const sourceMap = useMemo(() => {
        const map = new Map<number, PoplachTechnikaResponse>();
        technikaSources?.forEach(t => map.set(t.technikaId, t));
        return map;
    }, [technikaSources]);

    // Filtrovat techniku podle ID z poplach_technika
    const matchedTechnika = useMemo(() => {
        if (!allTechnika || !aktivniPoplach.technikaIds?.length) return [];
        return allTechnika.filter(t => aktivniPoplach.technikaIds!.includes(t.id));
    }, [allTechnika, aktivniPoplach.technikaIds]);

    const technikaCount = matchedTechnika.slice(0, 3).length;
    const isWide = isResponzivni && technikaCount <= 1;

    // Rozdělit odpovědi na JEDU / NEJEDU + spočítat role
    const { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH } = useMemo(() => {
        const jedouOdpovedi = odpovedi.filter(o => o.odpoved === 'JEDU');
        const nejedouOdpovedi = odpovedi.filter(o => o.odpoved === 'NEJEDU');
        let pocetVD = 0, pocetS = 0, pocetH = 0;
        for (const o of jedouOdpovedi) {
            if (o.funkce) {
                const kody = o.funkce.map(f => f.kod);
                if (kody.includes('VD')) pocetVD++;
                if (kody.includes('S')) pocetS++;
                if (kody.includes('H')) pocetH++;
            }
        }
        return { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH };
    }, [odpovedi]);

    // Adaptivní layout — větší font v každé skupině zvlášť (sekce s ≤ 4 členy se zvětší)
    const ADAPTIVE_THRESHOLD = 4;
    const useAdaptiveJedu = adaptivniLayout && jedouOdpovedi.length <= ADAPTIVE_THRESHOLD;
    const useAdaptiveNejedu = adaptivniLayout && nejedouOdpovedi.length <= ADAPTIVE_THRESHOLD;
    const useAdaptiveAny = useAdaptiveJedu || useAdaptiveNejedu;

    const jmenoFontFor = (large: boolean) => large ? '1.4rem' : (isWide ? '1.1rem' : '0.85rem');
    const chipFontSizeFor = (large: boolean) => large ? '0.8rem' : (isWide ? '0.65rem' : '0.5rem');
    const chipHeightFor = (large: boolean) => large ? 22 : (isWide ? 18 : 14);
    const itemPadYFor = (large: boolean) => large ? 0.6 : (isWide ? 0.5 : 0.25);

    // Sekce wrap (vnější Box kolem JEDU/NEJEDU sloupce) — žádné pozadí (COLUMNS_TINTED odstraněno)
    const sectionWrapSx = (_jedu: boolean) => ({});

    // Item style (vnitřní Box s jménem)
    // STRIPE má barevný borderLeft proužek;
    // BACKGROUND_TINTED má jemný barevný background u jména + proužek navíc.
    // flexWrap: 'nowrap' zaručuje, že chip s funkcí/specializací zůstane na stejném řádku jako jméno.
    const itemStyleSx = (jedu: boolean, large: boolean, nestiha = false) => {
        const barvaProuzku = nestiha ? theme.palette.warning.main : (jedu ? stripeJedu : stripeNejedu);
        const base = {
            px: 1,
            py: itemPadYFor(large),
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap' as const,
            gap: 0.5,
            breakInside: 'avoid' as const,
            borderRadius: 0.5,
            borderLeft: '3px solid',
            borderLeftColor: barvaProuzku,
            minWidth: 0,
        };
        // Oranzovy podklad i ve STRIPE: "cela kolonka oranzove" (HO-162)
        if (nestiha) return { ...base, backgroundColor: podkladNestiha };
        if (displejStyl === 'BACKGROUND_TINTED') {
            return { ...base, backgroundColor: jedu ? cardTintJedu : cardTintNejedu };
        }
        return base;
    };

    // Dynamicky vypocitat kolik jmen se vejde do karty odpovedi
    const odpovediRef = useRef<HTMLDivElement>(null);
    const [maxItems, setMaxItems] = useState(20);

    useEffect(() => {
        const el = odpovediRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            const h = el.clientHeight;
            const padding = 16; // p: 1 = 8px top + 8px bottom
            // role row: fontSize 1.1rem*1.5 lineHeight=26px + py 4px + border 2px + mt 6px ≈ 38px
            // wide role: fontSize 1.3rem*1.5=31px + py 4px + border 2px + mt 6px ≈ 43px
            const roleRowH = showRole ? (isWide ? 43 : 38) : 0;
            // item: fontSize*1.5 lineHeight + py + mb 4px
            // adaptive: 1.4rem*1.5=33px + 9.6px py + 4px mb ≈ 47px
            // wide: 1.1rem*1.5=26px + 8px py + 4px mb ≈ 38px
            // normal: 0.85rem*1.5=20px + 4px py + 4px mb ≈ 28px
            // Adaptive mód s flexWrap: nowrap drží jméno+chip+čas na jednom řádku;
            // 1.4rem * 1.5 lineHeight = ~33px + py 0.6 (≈10px) + mb 0.5 (4px) = ~50px reálně.
            const itemH = useAdaptiveAny ? 52 : (isWide ? 38 : 28);
            setMaxItems(Math.max(1, Math.floor((h - padding - roleRowH) / itemH)));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [isWide, showRole, useAdaptiveAny]);

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'stretch' }}>
                {/* Karty techniky (max 3) */}
                {matchedTechnika.slice(0, 3).map(t => {
                    const source = sourceMap.get(t.id);
                    return (
                    <Paper key={t.id} elevation={1} sx={getTechnikaCardSx(technikaCount)}>
                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                            <Typography sx={{ fontSize: technikaCount === 3 ? '2.2rem' : '3rem', fontWeight: 900, color: 'error.main', wordBreak: 'break-word', lineHeight: 1.1 }}>
                                {t.volaciZnak}
                            </Typography>
                            <Typography sx={{ fontSize: technikaCount === 3 ? '1.1rem' : '1.5rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                {t.nazev}
                            </Typography>
                            {t.spz && (
                                <Typography sx={{ fontSize: technikaCount === 3 ? '0.8rem' : '1rem', color: 'text.disabled', lineHeight: 1.3, mt: 0.5 }}>
                                    {t.spz}
                                </Typography>
                            )}
                            {source && (
                                <Chip
                                    size="small"
                                    label={
                                        source.zdroj === 'KOPIS'
                                            ? 'KOPIS'
                                            : source.potvrzenoKopis
                                                ? `Velitel ✓ KOPIS`
                                                : 'Velitel'
                                    }
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 700,
                                        fontSize: technikaCount === 3 ? '0.6rem' : '0.7rem',
                                        color: '#fff',
                                        bgcolor: source.zdroj === 'KOPIS'
                                            ? 'info.main'
                                            : source.potvrzenoKopis
                                                ? 'success.main'
                                                : 'warning.main',
                                    }}
                                />
                            )}
                        </Box>
                        <TechnikaFoto
                            technikaId={t.id}
                            maFotografii={t.maFotografii}
                            alt={t.nazev}
                            deviceToken={deviceToken}
                            objectFit="contain"
                            ikonaFontSize={technikaCount === 3 ? 48 : 64}
                            sx={{ width: technikaCount === 3 ? '50%' : '60%', aspectRatio: '4/3' }}
                        />
                    </Paper>
                    );
                })}

                {/* Prázdné sloty aby karta odpovědí byla vždy ve třetím (pravém) slotu */}
                {!isResponzivni && technikaCount === 0 && (
                    <>
                        <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                        <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                    </>
                )}
                {!isResponzivni && technikaCount === 1 && (
                    <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                )}

                {/* Karta odpovědí */}
                <Paper
                    elevation={1}
                    sx={{
                        ...UCAST_CARD_SX,
                        ...(isWide && technikaCount === 1 && {
                            flex: '1 1 calc(66.666% - 8px)',
                            maxWidth: 'calc(66.666% - 6px)',
                        }),
                        ...(isWide && technikaCount === 0 && {
                            flex: '1 1 100%',
                            maxWidth: '100%',
                        }),
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Neviditelny element - urcuje stejnou vysku jako karta techniky (60% z 33% slotu) */}
                    <Box sx={{ width: isWide && technikaCount === 1 ? '30%' : isWide && technikaCount === 0 ? '20%' : '60%', aspectRatio: '4/3', ml: 'auto', visibility: 'hidden', flex: '0 0 auto' }} />
                    <Box ref={odpovediRef} sx={{ position: 'absolute', inset: 0, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Horní část: jména + semafor */}
                        <Box sx={{ flex: 1, display: 'flex', gap: 1, minHeight: 0, overflow: 'hidden' }}>
                            {/* JEDU sloupec */}
                            <Box sx={{ flex: 1, minWidth: 0, ...(isWide && { columns: 2, columnGap: '8px' }), ...sectionWrapSx(true) }}>
                                {((isWide ? jedouOdpovedi.length > maxItems * 2 : jedouOdpovedi.length > maxItems)
                                    ? jedouOdpovedi.slice(0, (isWide ? maxItems * 2 : maxItems) - 1)
                                    : jedouOdpovedi
                                ).map(o => {
                                    const nestiha = jeNestihajici(o);
                                    return (
                                        <Box
                                            key={o.id}
                                            data-testid="ucast-jedu-polozka"
                                            data-nestiha={nestiha ? 'true' : 'false'}
                                            sx={itemStyleSx(true, useAdaptiveJedu, nestiha)}
                                        >
                                            <Typography fontSize={jmenoFontFor(useAdaptiveJedu)} fontWeight="bold" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
                                                {o.jmeno}
                                            </Typography>
                                            {o.dojezdCas && aktivniPoplach.casVyhlaseni && (
                                                <DojezdOdpocet
                                                    dojezdCas={o.dojezdCas}
                                                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                                                    fontSize={jmenoFontFor(useAdaptiveJedu)}
                                                />
                                            )}
                                            {showSpecializace && o.specializace?.map(s => (
                                                <Chip key={s.id} label={s.kod} size="small" variant="outlined" color="secondary"
                                                      sx={{ fontSize: chipFontSizeFor(useAdaptiveJedu), height: chipHeightFor(useAdaptiveJedu), '& .MuiChip-label': { px: 0.5 } }} />
                                            ))}
                                        </Box>
                                    );
                                })}
                                {(isWide ? jedouOdpovedi.length > maxItems * 2 : jedouOdpovedi.length > maxItems) && (
                                    <Typography fontSize="0.75rem" color="text.secondary" sx={{ pl: 1 }}>
                                        + {jedouOdpovedi.length - (isWide ? maxItems * 2 : maxItems) + 1} dalších
                                    </Typography>
                                )}
                            </Box>
                            {/* NEJEDU sloupec */}
                            <Box sx={{ flex: 1, minWidth: 0, ...(isWide && { columns: 2, columnGap: '8px' }), ...sectionWrapSx(false) }}>
                                {((isWide ? nejedouOdpovedi.length > maxItems * 2 : nejedouOdpovedi.length > maxItems)
                                    ? nejedouOdpovedi.slice(0, (isWide ? maxItems * 2 : maxItems) - 1)
                                    : nejedouOdpovedi
                                ).map(o => (
                                    <Box
                                        key={o.id}
                                        sx={itemStyleSx(false, useAdaptiveNejedu)}
                                    >
                                        <Typography fontSize={jmenoFontFor(useAdaptiveNejedu)} fontWeight="bold" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
                                            {o.jmeno}
                                        </Typography>
                                        {showSpecializace && o.specializace?.map(s => (
                                            <Chip key={s.id} label={s.kod} size="small" variant="outlined" color="secondary"
                                                  sx={{ fontSize: chipFontSizeFor(useAdaptiveNejedu), height: chipHeightFor(useAdaptiveNejedu), '& .MuiChip-label': { px: 0.5 } }} />
                                        ))}
                                    </Box>
                                ))}
                                {(isWide ? nejedouOdpovedi.length > maxItems * 2 : nejedouOdpovedi.length > maxItems) && (
                                    <Typography fontSize="0.75rem" color="text.secondary" sx={{ pl: 1 }}>
                                        + {nejedouOdpovedi.length - (isWide ? maxItems * 2 : maxItems) + 1} dalších
                                    </Typography>
                                )}
                            </Box>
                            {/* Počítadlo sloupec */}
                            {showSemafor && (
                            <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, minWidth: isWide ? 100 : 80 }}>
                                <Box sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 0.5,
                                    px: 1,
                                    flex: 1,
                                }}>
                                    <Typography fontWeight="bold" fontSize={isWide ? '1rem' : '0.85rem'} lineHeight={1}>
                                        JEDU
                                    </Typography>
                                    <Typography fontWeight="bold" fontSize={isWide ? '3.5rem' : '3rem'} lineHeight={1} mt={0.75}>
                                        {jedouOdpovedi.length}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 0.5,
                                    px: 1,
                                    flex: 1,
                                }}>
                                    <Typography fontWeight="bold" fontSize={isWide ? '1rem' : '0.85rem'} lineHeight={1}>
                                        NEJEDU
                                    </Typography>
                                    <Typography fontWeight="bold" fontSize={isWide ? '3.5rem' : '3rem'} lineHeight={1} mt={0.75}>
                                        {nejedouOdpovedi.length}
                                    </Typography>
                                </Box>
                            </Box>
                            )}
                        </Box>
                        {/* Spodní řádek: role */}
                        {showRole && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flex: '0 0 auto' }}>
                            {[
                                { label: 'Velitel družstva', count: pocetVD },
                                { label: 'Strojník', count: pocetS },
                                { label: 'Hasič', count: pocetH },
                            ].map(r => (
                                <Box key={r.label} sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.25,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}>
                                    <Typography fontSize={isWide ? '0.9rem' : '0.75rem'} fontWeight="bold">{r.label}</Typography>
                                    <Typography fontSize={isWide ? '1.3rem' : '1.1rem'} fontWeight="900" color="primary.main">{r.count}</Typography>
                                </Box>
                            ))}
                        </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default React.memo(TechnikaUcastWrapper);
